import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import { FeatureFlagsProvider } from '@fs/zion-flags'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout } from '@fs/zion-ui'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { NoticeLoading } from '@fs/zion-icon'
import useAlertBanner from './components/hooks/useAlertBanner'
import AlertBannerContext from './components/hooks/AlertBannerContext'

import App from './App'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import './locales'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
// const mockFlags = undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
    })
  )
}

// This is where you pass data from the server to the client using the SERVER_DATA global.
// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const FrontierRoot = () => {
  const alertBannerProps = useAlertBanner()
  return (
    <React.StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders>
          <FeatureFlagsProvider mockFlags={mockFlags}>
            <Router basename={base}>
              <Root analytics alertBannerProps={alertBannerProps}>
                <AlertBannerContext.Provider value={alertBannerProps}>
                  <AppLayout fullWidth>
                    <App />
                  </AppLayout>
                </AlertBannerContext.Provider>
              </Root>
            </Router>
          </FeatureFlagsProvider>
        </FrontierBaseProviders>
      </Suspense>
    </React.StrictMode>
  )
}

ReactDOM.render(
  <Suspense fallback={<NoticeLoading />}>
    <FrontierRoot />
  </Suspense>,
  document.getElementById('root')
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister()
